import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import type { Me } from '../lib/session.js';
import {
  CLOSED_REASON_LABEL,
  TICKET_STATUS_LABEL,
  TRIP_STATE_LABEL,
  label,
} from '../lib/labels.js';
import { date, num } from '../lib/format.js';
import {
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';

interface TripRow {
  tripId: string;
  contactId: string;
  raceId: string;
  raceName: string;
  seller: string;
  state: string;
  closedReason: string | null;
  leadDays: number | null;
  ticketState: string | null;
  flexiState: string;
  careHold: boolean;
  email: string;
  dateStart: string | null;
}

const FLEXI_LABEL: Record<string, string> = {
  none: 'Bez Flexi',
  active: 'Aktivní',
  claim_in_progress: 'Žádost běží',
  used: 'Využité',
};

export function Trips({ me }: { me: Me }): JSX.Element {
  const [state, setState] = useState('');
  const [seller, setSeller] = useState('');
  const [leadBand, setLeadBand] = useState('');
  const [flexi, setFlexi] = useState('');
  const [tickets, setTickets] = useState('');

  const list = useQuery<{ items: TripRow[]; total: number }>({
    queryKey: ['trips', state, seller, leadBand, flexi, tickets],
    queryFn: () =>
      api.get(`/api/trips${qs({ state, seller, leadBand, flexi, tickets, pageSize: 100 })}`),
  });
  const states = useQuery<{ items: Array<{ state: string; n: number }> }>({
    queryKey: ['trip-states'],
    queryFn: () => api.get('/api/trips/states'),
  });

  return (
    <>
      <PageHeader
        title="Cesty"
        description="Cesta je jeden zákazník na jednom závodě u jednoho prodejce. Vzniká z objednávek a její stav se dopočítává z kalendáře závodu. Všímejte si cest, které čekají na termín nebo na vstupenky těsně před závodem."
      />

      <div className="s2a-filters">
        <Field label="Stav cesty">
          <select value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">Všechny stavy</option>
            {Object.entries(TRIP_STATE_LABEL).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        </Field>
        <BrandSelect value={seller} onChange={setSeller} />
        <Field label="Předstih nákupu">
          <select value={leadBand} onChange={(e) => setLeadBand(e.target.value)}>
            <option value="">Jakýkoli předstih</option>
            <option value="0-7">0–7 dní</option>
            <option value="8-29">8–29 dní</option>
            <option value="30-59">30–59 dní</option>
            <option value="60-179">60–179 dní</option>
            <option value="180+">180+ dní</option>
            <option value="unknown">Neznámý</option>
          </select>
        </Field>
        <Field label="Pojištění Flexi">
          <select value={flexi} onChange={(e) => setFlexi(e.target.value)}>
            <option value="">Jakékoli</option>
            <option value="none">Bez Flexi</option>
            <option value="active">Aktivní</option>
            <option value="claim_in_progress">Žádost běží</option>
            <option value="used">Využité</option>
          </select>
        </Field>
        <Field label="Vstupenky">
          <select value={tickets} onChange={(e) => setTickets(e.target.value)}>
            <option value="">Jakékoli</option>
            {Object.entries(TICKET_STATUS_LABEL).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
            <option value="unknown">Neznámé</option>
          </select>
        </Field>
      </div>

      {states.data ? (
        <div className="row s2a-mb" aria-label="Počty cest podle stavu">
          {states.data.items.map((s) => (
            <button
              key={s.state}
              className="btn"
              onClick={() => setState(s.state)}
              aria-pressed={state === s.state}
            >
              {label(TRIP_STATE_LABEL, s.state)} <b>{num(s.n)}</b>
            </button>
          ))}
          {state ? (
            <button className="btn" onClick={() => setState('')}>
              Zrušit filtr
            </button>
          ) : null}
        </div>
      ) : null}

      {list.isLoading ? <LoadingState what="cesty" /> : null}
      {list.isError ? (
        <ErrorState
          title="Cesty se nepodařilo načíst"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : null}

      <Panel title={`Cesty (${num(list.data?.total ?? 0)})`}>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Zákazník</th>
                <th>Závod</th>
                <th>Prodejce</th>
                <th>Stav</th>
                <th className="n">Předstih</th>
                <th>Vstupenky</th>
                <th>Flexi</th>
                <th>Péče</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((t) => (
                <tr key={t.tripId}>
                  <td>
                    <a href={hrefFor('customers', t.contactId)}>{t.email}</a>
                  </td>
                  <td>
                    <a href={hrefFor('races', t.raceId)}>{t.raceName}</a>
                    <div className="muted">{date(t.dateStart, me.defaultTimezone)}</div>
                  </td>
                  <td>{t.seller}</td>
                  <td>
                    {label(TRIP_STATE_LABEL, t.state)}
                    {t.closedReason ? (
                      <div className="muted">{label(CLOSED_REASON_LABEL, t.closedReason)}</div>
                    ) : null}
                  </td>
                  <td className="n">{t.leadDays === null ? '—' : `${t.leadDays} d`}</td>
                  <td>{t.ticketState ? label(TICKET_STATUS_LABEL, t.ticketState) : '—'}</td>
                  <td>{t.flexiState === 'none' ? '—' : label(FLEXI_LABEL, t.flexiState)}</td>
                  <td>
                    {t.careHold ? (
                      <StatusBadge tone="warn" icon="alert">
                        Marketing pozastaven
                      </StatusBadge>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data && list.data.items.length === 0 ? (
          <EmptyState title="Filtru nic neodpovídá">
            Zkuste některý filtr zrušit. Cesty vznikají z objednávek, takže před prvním importem
            objednávek je seznam prázdný.
          </EmptyState>
        ) : null}
      </Panel>
    </>
  );
}
